import { useState } from "react";
import { NotePencil, PhoneDisconnect, ThumbsDown, ThumbsUp } from "@phosphor-icons/react";
import { getUser } from "../../auth";
import { STARTER_PILLS } from "../starterPills";

const FOLLOW_UP = {
  up: {
    prompt: "Great, what went well?",
    reasons: ["Efficient", "Helpful resolution", "Easy to use", "Friendly tone"],
  },
  down: {
    prompt: "We are sorry to hear that. Can you tell us more?",
    reasons: ["Not efficient", "No helpful resolution", "Hard to understand me", "Unfriendly tone"],
  },
} as const;

type Rating = keyof typeof FOLLOW_UP;

// Rendered under the frozen transcript once a conversation ends: thumbs rating,
// optional reason chips, then a thank-you with the quick starts. A "Start a new
// chat" button is always there, so rating is optional: it resets to a fresh,
// auto-greeted chat right away (the same reset as the header's New chat).
// Submitting posts to the widget server's /api/feedback, which forwards to
// PostHog (see src/feedback.ts).
export function ConversationFeedback({
  serverUrl,
  conversationId,
  endedBy,
  channel,
  onStartNew,
  onStartFresh,
}: {
  serverUrl: string;
  conversationId: string;
  endedBy: "user" | "agent";
  channel: "voice" | "chat";
  onStartNew: (message: string) => void;
  onStartFresh: () => void;
}) {
  const [rating, setRating] = useState<Rating | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const rate = (next: Rating) => {
    setRating(next);
    setReasons([]); // the two ratings have different chip sets
  };

  const toggleReason = (reason: string) =>
    setReasons((cur) => (cur.includes(reason) ? cur.filter((r) => r !== reason) : [...cur, reason]));

  const submit = () => {
    if (!rating) return;
    setSubmitted(true);
    // Fire-and-forget: thank the user right away rather than block on the send.
    // keepalive lets it finish if they close the tab straight after.
    fetch(`${serverUrl}/api/feedback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        rating,
        reasons,
        conversation_id: conversationId,
        channel,
        ended_by: endedBy,
        user_id: getUser()?.id ?? null,
      }),
    })
      .then((resp) => {
        if (!resp.ok) console.error(`[lk-widget] feedback send failed: ${resp.status}`);
      })
      .catch((err) => console.error("[lk-widget] feedback send failed:", err));
  };

  if (submitted) {
    return (
      <div className="lk-widget-feedback">
        <p className="lk-widget-feedback-thanks">Thank you for your feedback!</p>
        <p className="lk-widget-feedback-q">What would you like to do next?</p>
        <div className="lk-widget-feedback-chips lk-widget-feedback-chips--stacked">
          {STARTER_PILLS.map((pill) => (
            <button
              key={pill.label}
              type="button"
              className="lk-widget-feedback-chip"
              onClick={() => onStartNew(pill.message)}
            >
              <pill.Icon size={14} weight="regular" />
              {pill.label}
            </button>
          ))}
        </div>
        <button type="button" className="lk-widget-feedback-new" onClick={onStartFresh}>
          <NotePencil size={15} weight="bold" />
          Start a new chat
        </button>
      </div>
    );
  }

  return (
    <div className="lk-widget-feedback">
      {channel === "voice" && (
        <div className="lk-widget-feedback-ended">
          <span className="lk-widget-feedback-ended-icon">
            <PhoneDisconnect size={16} weight="regular" />
          </span>
          <p>{endedBy === "user" ? "You ended the conversation" : "Courtney ended the conversation"}</p>
          {conversationId && <p>ID: {conversationId}</p>}
        </div>
      )}

      <p className="lk-widget-feedback-q">Overall, how would you rate your booking experience?</p>
      <div className="lk-widget-feedback-thumbs">
        <button
          type="button"
          className="lk-widget-feedback-thumb"
          aria-pressed={rating === "up"}
          aria-label="Good experience"
          onClick={() => rate("up")}
        >
          <ThumbsUp size={16} weight="regular" />
        </button>
        <button
          type="button"
          className="lk-widget-feedback-thumb"
          aria-pressed={rating === "down"}
          aria-label="Bad experience"
          onClick={() => rate("down")}
        >
          <ThumbsDown size={16} weight="regular" />
        </button>
      </div>

      {rating && (
        <>
          <p className="lk-widget-feedback-q lk-widget-feedback-q--follow-up">{FOLLOW_UP[rating].prompt}</p>
          <div className="lk-widget-feedback-chips">
            {FOLLOW_UP[rating].reasons.map((reason) => (
              <button
                key={reason}
                type="button"
                className="lk-widget-feedback-chip"
                aria-pressed={reasons.includes(reason)}
                onClick={() => toggleReason(reason)}
              >
                {reason}
              </button>
            ))}
          </div>
          <button type="button" className="lk-widget-feedback-submit" onClick={submit}>
            Submit
          </button>
        </>
      )}

      <button type="button" className="lk-widget-feedback-new" onClick={onStartFresh}>
        <NotePencil size={15} weight="bold" />
        Start a new chat
      </button>
    </div>
  );
}
